import { useMutation, useQuery } from "urql";
import { graphql } from "../../../../__generated__/gql";
import Modal from "../../../../ui/Modal/Modal";
import { useCallback } from "react";
import { UpdateArticlesInput } from "../../../../__generated__/gql/graphql";
import { useToast } from "../../../../ui/Toast/ToastContext";
import Label from "../../../../ui/Label/Label";
import { Controller, useForm } from "react-hook-form";
import Select from "../../../../ui/Select/Select";
import { z } from "zod";
import TagSelect from "../../../../components/TagSelect/TagSelect";
import Textarea from "../../../../ui/Textarea/Textarea";
import styles from "./UpdateArticlesModal.module.scss";
import ConfirmationModal from "../../../../ui/Modal/ConfirmationModal";
import { zodResolver } from "@hookform/resolvers/zod";

const UpdateArticlesModalQuery = graphql(/* GraphQL */ `
  query updateArticlesModal($filters: [ArticleFilter!]) {
    articleCounts(input: { filters: $filters }) {
      total
    }
  }
`);

const UpdateArticlesMutation = graphql(/* GraphQL */ `
  mutation updateArticles($input: UpdateArticlesInput!) {
    updateArticles(input: $input) {
      count
    }
  }
`);

const schema = z.object({
  property: z.enum(["tag", "customPrompt"]),
  tag: z.string().optional(),
  customPrompt: z.string().optional(),
});

type UpdateArticlesFormData = z.infer<typeof schema>;

function InternalUpdateArticlesModal({
  onClose,
  onSubmit,
  loading,
}: {
  onClose: () => void;
  onSubmit: (data: UpdateArticlesFormData) => Promise<void>;
  loading: boolean;
}) {
  const { control, handleSubmit, watch } = useForm<UpdateArticlesFormData>({
    resolver: zodResolver(schema),
  });

  const property = watch("property");

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
      <ConfirmationModal onClose={onClose} loading={loading}>
        <Label label="Property">
          <Controller
            control={control}
            name="property"
            render={({ field }) => (
              <Select
                {...field}
                withinPortal
                placeholder="Select a property to update"
                data={[
                  { value: "tag", label: "Tag" },
                  { value: "customPrompt", label: "Custom prompt" },
                ]}
              />
            )}
          />
        </Label>
        {property === "tag" && (
          <Label label="Tag">
            <Controller
              control={control}
              name="tag"
              render={({ field }) => <TagSelect {...field} />}
            />
          </Label>
        )}
        {property === "customPrompt" && (
          <Label label="Custom prompt">
            <Controller
              control={control}
              name="customPrompt"
              render={({ field }) => <Textarea {...field} />}
            />
          </Label>
        )}
      </ConfirmationModal>
    </form>
  );
}

function UpdateArticlesModal({
  isOpen,
  onClose,
  filters,
}: {
  isOpen: boolean;
  onClose: () => void;
  filters: UpdateArticlesInput["filters"];
}) {
  const { showToast } = useToast();
  const [{ fetching }, updateArticles] = useMutation(UpdateArticlesMutation);
  const [updateArticlesModalResponse] = useQuery({
    query: UpdateArticlesModalQuery,
    pause: !isOpen,
    variables: { filters },
  });

  const articleCounts = updateArticlesModalResponse.data?.articleCounts;
  const totalArticles = articleCounts ? articleCounts.total : 0;

  const onSubmit = useCallback(
    async (data: UpdateArticlesFormData) => {
      await updateArticles({
        input: {
          filters,
          tag: data.property === "tag" ? data.tag : undefined,
          additionalPrompt:
            data.property === "customPrompt" ? data.customPrompt : undefined,
        },
      });

      showToast({
        title: `${totalArticles} ${
          totalArticles === 1 ? "article" : "articles"
        } updated`,
      });

      onClose();
    },
    [updateArticles, filters, showToast, totalArticles, onClose],
  );

  const title = `Bulk edit ${totalArticles} ${
    totalArticles === 1 ? "article" : "articles"
  }`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      loading={
        updateArticlesModalResponse.fetching ||
        !updateArticlesModalResponse.data
      }
    >
      <InternalUpdateArticlesModal
        onClose={onClose}
        onSubmit={onSubmit}
        loading={fetching || !isOpen}
      />
    </Modal>
  );
}

export default UpdateArticlesModal;
